"use client";
import React, { useEffect, useState, useTransition } from "react";
import { useDispatch } from "react-redux";

import { useAppSelector } from "../../../app/GlobalRedux/store";
import HibtService from "@/services/hibtService";
import { ListTypeId, SearchResultItem, SearchType } from "@/types";
import { itemsPerPageOptions } from "@/utils/consts";
import { useRouter, useSearchParams } from "next/navigation";
import { useGoogleReCaptcha } from "react-google-recaptcha-hook";
import LoadingState from "../../states/LoadingState";
import SearchPageLayout from "../../Layouts/SearchPageLayout/SearchPageLayout";
import InfoSection from "../../InfoSection/InfoSection";
import TableControls from "../../Table/TableControls/TableControls";
import Table from "../../Table/Table";
import PaginationWrapper from "../../PaginationWrapper/PaginationWrapper";
import { setSnackBar } from "../../../app/GlobalRedux/Features/snackBarSlice";
import EmptyState from "@/components/states/EmptyState/EmptyState";
import ErrorState from "@/components/states/ErrorState/ErrorState";
import Logging from "@/utils/Logging";
import { set } from "react-hook-form";

const TextSearchResultsPage = () => {
  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    process.env.NEXT_PUBLIC_REACT_GACAPTV3_SITE_KEY || "",
    { hide: true }
  );

  const dispatch = useDispatch();
  const currentSearchValue = useAppSelector((state) => state.search.value);
  const user = useAppSelector((state) => state.auth.user);
  const lists = useAppSelector((state) => state.auth.lists);
  const { doNotTrainList } = useAppSelector((state) => state.doNotTrainData);

  const [doNotTrainListData, setDoNotTrainListData] = useState(doNotTrainList);

  const searchParams = useSearchParams();
  const searchValue = searchParams.get("search_text") || currentSearchValue;
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [textSearchDataTotal, setTextSearchDataTotal] = useState(0);
  const [textSearchData, setTextSearchData] = useState<SearchResultItem[]>([]);
  const [textSearchDataToDisplay, setTextSearchDataToDisplay] = useState<
    SearchResultItem[]
  >([]);

  // pagination items
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageOptions[0].value
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    count: 0,
    from: 0,
    to: itemsPerPage,
  });
  const [hasMore, setHasMore] = useState(false);
  const limitSize = 20;
  const maxItems = 1000;
  let skip = 0;
  let lazyLoadTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  const controller = new AbortController();

  const loadSearchResultsData = async () => {
    const isFirstLoad = skip === 0;

    const captchaKey = await executeGoogleReCaptcha("submit");

    if (isFirstLoad) {
      setIsLoading(true);
    }

    try {
      const { records, has_more } = await HibtService.search({
        text: searchValue || undefined,
        CaptchaResponse: captchaKey,
        skip: skip,
        limit: limitSize,
        signal: controller.signal,
      });

      // ... existing logic to fetch data
      const transformedData = records.map((searchResult) => ({
        ...searchResult,
        isChecked: false,
      }));

      // Filter out items that are in the "Do Not Train" list
      const filteredData = transformedData.filter(
        (item) => !doNotTrainListData.map(({ url }) => url).includes(item.url)
      );

      // refactor above code to concat the results with the current values
      // and then set the new values
      setTextSearchData((prevData) => [...prevData, ...filteredData]);
      setTextSearchDataTotal((prevTotal) => prevTotal + filteredData.length);
      setPagination((prevPagination) => ({
        ...prevPagination,
        count: prevPagination.count + filteredData.length,
      }));

      // display the page
      if (isFirstLoad) {
        setIsLoading(false);
        startTransition(() => {
          router.refresh();
        });
      }

      // if we're at the end (probably) then stop
      if (skip >= maxItems || !has_more) {
        setIsLoading(false);
        setHasMore(false);
        return;
      }

      setHasMore(has_more);

      // schedule the next call
      lazyLoadTimeout = setTimeout(async () => {
        skip += limitSize;
        loadSearchResultsData();
      }, 1500);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setIsLoading(false);
      setErrorMessage(error.message);

      Logging.networkError(`API Error - HIBTservice search ${searchValue}`, {
        pageName: "TextSearchResultsPage",
        errorMessage: error.message,
        errorCode: error.code,
        statusCode: error.response?.status,
      });

      dispatch(
        setSnackBar({
          open: true,
          message: `An error occurred: ${error.message}`,
          variant: "error",
        })
      );
    }
  };

  useEffect(() => {
    skip = 0;
    clearLazyLoadingTimeout();
    setCurrentPage(1);
    setErrorMessage(null);

    // Reset pagination
    setPagination({
      count: 0,
      from: 0,
      to: itemsPerPage,
    });

    setTextSearchDataTotal(0); // Reset total before loading new results
    setTextSearchData([]); // Reset data before loading new results
    loadSearchResultsData();

    return () => {
      clearLazyLoadingTimeout();
      controller.abort();
    };
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // have to have this function so we don't pass in the initial value
  // of the timeout but the current value
  function clearLazyLoadingTimeout() {
    clearTimeout(lazyLoadTimeout);
  }

  useEffect(() => {
    const paginatedData = textSearchData.slice(pagination.from, pagination.to);

    setTextSearchDataToDisplay(paginatedData);
  }, [textSearchData, pagination.from, pagination.to]);

  useEffect(() => {
    // calculate the new page number based on the new items per page
    let newPageNumber = Math.ceil((pagination.from + 1) / itemsPerPage);
    if (newPageNumber <= 0) newPageNumber = 1;
    setCurrentPage(newPageNumber);

    // calculate new from and to
    const from = (newPageNumber - 1) * itemsPerPage;
    const to = itemsPerPage * newPageNumber;

    setPagination({ ...pagination, from, to });
  }, [itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptingOutItems = async () => {
    if (!user || !lists) return;

    setIsLoading(true);

    const selectedItems = textSearchData.filter(({ isChecked }) => isChecked);
    const doNotTrainList = lists.find(
      ({ type_id }) => type_id === ListTypeId.DO_NOT_TRAIN
    );

    const dataToSend = selectedItems.map(({ id, url, caption }) => ({
      id,
      url,
      caption,
      type_id: ListTypeId.DO_NOT_TRAIN,
      list_id: doNotTrainList?.id || 0,
    }));

    try {
      const optOutResponse =
        await HibtService.insertMultipleObjectsToSelections(
          user.id_token,
          dataToSend
        );

      if (optOutResponse.status === 200) {
        // Get IDs of items opted out
        const optedOutUrls = selectedItems.map((item) => item.url);

        // Update local "Do Not Train" list state
        setDoNotTrainListData((prevList) => [...prevList, ...selectedItems]);

        // Filter out opted-out items from the current display and the main data
        const updatedDisplayData = textSearchDataToDisplay.filter(
          (item) => !optedOutUrls.includes(item.url)
        );

        const updatedTextSearchData = textSearchData.filter(
          (item) => !optedOutUrls.includes(item.url)
        );

        setTextSearchDataToDisplay(updatedDisplayData);
        setTextSearchData(updatedTextSearchData);
        setTextSearchDataTotal(updatedTextSearchData.length);
        setPagination({ ...pagination, count: updatedTextSearchData.length });

        dispatch(
          setSnackBar({
            open: true,
            message: `${dataToSend.length} ${
              dataToSend.length === 1 ? "item" : "items"
            } add to your`,
            variant: "doNotTrain",
          })
        );
      } else {
        // Handle non-200 response
        dispatch(
          setSnackBar({
            open: true,
            message: `Failed to add items to your "Do Not Train" list`,
            variant: "doNotTrainError",
          })
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      Logging.networkError(`API Error - handle opting out items`, {
        pageName: "TextSearchResultsPage",
        errorMessage: error.message,
        errorCode: error.code,
        statusCode: error.response?.status,
      });

      dispatch(
        setSnackBar({
          open: true,
          message: `An error occurred: ${error.message}`,
          variant: "error",
        })
      );
    }

    setIsLoading(false);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    event.preventDefault();

    const from = (page - 1) * itemsPerPage;
    const to = itemsPerPage * page;

    setCurrentPage(page);
    setPagination({ ...pagination, from, to });

    if (window && typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const handleCheckBoxClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    url: string
  ) => {
    const { checked } = e.target;

    // update current selections
    /*const newSelectionArray = textSearchDataToDisplay.map((searchResult) =>
      searchResult.url === url
        ? { ...searchResult, isChecked: checked }
        : searchResult
    );*/

    const newTextSearchData = textSearchData.map((searchResult) =>
      searchResult.url === url
        ? { ...searchResult, isChecked: checked }
        : searchResult
    );

    setTextSearchData(newTextSearchData);

    //setTextSearchDataToDisplay(newSelectionArray);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    url: string
  ) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).checked = true;

      const { checked } = e.target as HTMLInputElement;

      const newSelectionArray = textSearchData.map((searchResult) =>
        searchResult.url === url
          ? { ...searchResult, isChecked: checked }
          : searchResult
      );

      setTextSearchData(newSelectionArray);
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      (e.target as HTMLInputElement).checked = false;

      const { checked } = e.target as HTMLInputElement;

      const newSelectionArray = textSearchData.map((searchResult) =>
        searchResult.url === url
          ? { ...searchResult, isChecked: checked }
          : searchResult
      );

      setTextSearchData(newSelectionArray);
    }
  };

  const handleOnSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    // only check items on current pagination range

    const selectAllItems = textSearchData.map((searchResult, index) => ({
      ...searchResult,
      isChecked:
        index >= pagination.from && index < pagination.to
          ? checked
          : searchResult.isChecked,
    }));

    setTextSearchData(selectAllItems);
  };

  const handleKeyDownSelectAll = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).checked = true;

      const { checked } = e.target as HTMLInputElement;

      const selectAllItems = textSearchData.map((searchresult, index) => ({
        ...searchresult,
        isChecked:
          index >= pagination.from && index < pagination.to
            ? checked
            : searchresult.isChecked,
      }));

      setTextSearchData(selectAllItems);
    }
  };

  if (isLoading || isPending) {
    return <LoadingState />;
  }

  if (
    errorMessage &&
    // this most likely means a 504 error so we can ignore it so the rest of the page loads
    errorMessage !== "An unknown error has occurred. Please try again later."
  ) {
    return <ErrorState message={errorMessage} />;
  }

  return (
    <SearchPageLayout>
      <InfoSection
        valueSearched={{
          value: searchValue || "",
          type: SearchType.TEXT,
        }}
        label="Text search"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        paginationCount={pagination.count}
        handlePageChange={handlePageChange}
      />

      <TableControls
        totalItems={textSearchDataTotal}
        pageNumber={currentPage}
        displayedItems={textSearchDataToDisplay.length}
        checkedItems={
          textSearchDataToDisplay.filter(({ isChecked }) => isChecked).length
        }
        itemsPerPage={itemsPerPage}
        itemsPerPageOptions={itemsPerPageOptions}
        setItemsPerPage={setItemsPerPage}
        handleOnSelectAllClick={handleOnSelectAllClick}
        handleKeyDownSelectAll={handleKeyDownSelectAll}
        handleOptingOutItems={handleOptingOutItems}
      />

      {(textSearchDataToDisplay.length > 0 || hasMore) &&
      !isLoading &&
      !errorMessage ? (
        <Table
          dataToDisplay={textSearchDataToDisplay}
          handleUrlCheckBoxClick={handleCheckBoxClick}
          handleUrlKeyDown={handleKeyDown}
          hasMore={
            hasMore &&
            currentPage == Math.floor(pagination.count / itemsPerPage) + 1
          }
        />
      ) : (
        <EmptyState>
          We were unable to find any results for your search. Please try again
        </EmptyState>
      )}

      <PaginationWrapper
        currentPage={currentPage}
        paginationCount={pagination.count}
        itemsPerPage={itemsPerPage}
        handlePageChange={handlePageChange}
      />
    </SearchPageLayout>
  );
};

export default TextSearchResultsPage;
